/**
 * AdventureManager — gamification layer over a walking route.
 *
 * An Adventure is a "quest mode": the user picks a theme, gets a list
 * of GPS-triggered collectable tasks, and works through them by walking
 * close enough to each task's anchor point. Each completion plays a
 * narrator text via TTS, vibrates the phone, and persists to local state.
 *
 * Lifecycle: the consumer creates a manager via the library's
 * getAdventureManager() getter, calls loadAdventure(), then activate().
 * The manager subscribes to 'location:changed' events from the event
 * bus and re-computes tasksInRange on every GPS update.
 *
 * The React useAdventure() hook wraps this manager and re-exposes its
 * state through React state. Consumers never instantiate this class
 * directly — they go through the hook.
 */
const ADVENTURE_STATE_SCHEMA_VERSION = 1;
const LAST_COMPLETED_TASK_CLEAR_MS = 8000;
const DEFAULT_TTS_LANG = 'de-AT';
const DEFAULT_TTS_RATE = 0.92;
export class AdventureManager {
    constructor(bus, localState, audioBus) {
        this._config = null;
        this._adventure = null;
        this._tasksInRange = [];
        this._nextTask = null;
        this._nextTaskDistance = null;
        this._isSpeaking = false;
        this._lastCompletedTask = null;
        this._lastCompletedTimeout = null;
        this._listeners = new Set();
        this._unsubscribeLocation = null;
        this._currentUtterance = null;
        this._bus = bus;
        this._localState = localState;
        this._audioBus = audioBus;
        this._session = { completedTasks: [], startedAt: 0, isComplete: false };
        this._audioBus.register({ id: AdventureManager.SOURCE_ID, onPause: () => this._pauseSpeechExternally() }, 'adventure');
    }
    /**
     * Activate an adventure. Loads the definition from config.dataSource,
     * reads prior session state from localStorage if any, starts listening
     * to GPS updates. Calling this replaces any currently-active adventure.
     */
    activate(config) {
        this.deactivate();
        this._config = config;
        if (!config.adventureId)
            return;
        // Load adventure definition (v0.2.0: static only)
        if (config.dataSource.type !== 'static') {
            throw new Error('AdventureManager: only dataSource.type === "static" is supported in v0.2.0.');
        }
        const adventure = config.dataSource.adventures[config.adventureId];
        if (!adventure) {
            throw new Error(`AdventureManager: adventure '${config.adventureId}' not found in dataSource.adventures`);
        }
        this._adventure = adventure;
        // Load persisted session from localStorage, or start a fresh one
        const stored = this._localState.load('adventure', adventure.id, ADVENTURE_STATE_SCHEMA_VERSION);
        if (stored) {
            this._session = {
                completedTasks: stored.completedTasks,
                startedAt: stored.startedAt,
                isComplete: stored.isComplete,
            };
        }
        else {
            this._session = {
                completedTasks: [],
                startedAt: Date.now(),
                isComplete: false,
            };
            this._persist();
        }
        // Subscribe to GPS updates from the library's internal event bus
        const handler = (snap) => {
            if (snap.lat !== null && snap.lon !== null) {
                this._onLocationUpdate(snap.lat, snap.lon);
            }
        };
        this._bus.on('location:changed', handler);
        this._unsubscribeLocation = () => this._bus.off('location:changed', handler);
        // If external GPS coordinates are provided (debug / DEBUG_GPS mode),
        // use them immediately rather than waiting for the first location event.
        if (config.externalGps) {
            this._onLocationUpdate(config.externalGps.lat, config.externalGps.lng);
        }
        this._notify();
    }
    /** Cancel current adventure, unsubscribe from location, release audio bus. */
    deactivate() {
        if (this._unsubscribeLocation) {
            this._unsubscribeLocation();
            this._unsubscribeLocation = null;
        }
        if (this._currentUtterance) {
            window.speechSynthesis?.cancel();
            this._currentUtterance = null;
        }
        if (this._lastCompletedTimeout) {
            clearTimeout(this._lastCompletedTimeout);
            this._lastCompletedTimeout = null;
        }
        this._audioBus.release(AdventureManager.SOURCE_ID);
        this._isSpeaking = false;
        this._lastCompletedTask = null;
        this._adventure = null;
        this._config = null;
        this._tasksInRange = [];
        this._nextTask = null;
        this._nextTaskDistance = null;
        this._session = { completedTasks: [], startedAt: 0, isComplete: false };
        this._notify();
    }
    /** Mark a task as completed: persist, vibrate, TTS, fire events. */
    completeTask(taskId) {
        if (!this._adventure)
            return;
        if (this._session.completedTasks.includes(taskId))
            return;
        const task = this._adventure.tasks.find((t) => t.id === taskId);
        if (!task)
            return;
        this._session.completedTasks = [...this._session.completedTasks, taskId];
        this._session.isComplete = this._session.completedTasks.length >= this._adventure.tasks.length;
        this._persist();
        // Browser vibration (mobile). Silently fails on desktop.
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
            navigator.vibrate([100, 50, 100]);
        }
        // Set the lastCompletedTask flag for 8 seconds so the UI can show a toast
        this._lastCompletedTask = task;
        if (this._lastCompletedTimeout)
            clearTimeout(this._lastCompletedTimeout);
        this._lastCompletedTimeout = setTimeout(() => {
            this._lastCompletedTask = null;
            this._notify();
        }, LAST_COMPLETED_TASK_CLEAR_MS);
        // Speak the narrator text (if we can grab the audio bus)
        void this._speak(task.narratorText);
        this._bus.emit('adventure:task-completed', { task });
        this._notify();
    }
    /** Reset the current adventure's state completely. */
    reset() {
        if (!this._adventure)
            return;
        this._localState.remove('adventure', this._adventure.id);
        this._session = {
            completedTasks: [],
            startedAt: Date.now(),
            isComplete: false,
        };
        this._persist();
        this._notify();
    }
    /** Register a state-change listener (used by the React hook). */
    onStateChange(listener) {
        this._listeners.add(listener);
        listener(this.getState());
        return () => {
            this._listeners.delete(listener);
        };
    }
    /** Current immutable state snapshot. */
    getState() {
        const total = this._adventure?.tasks.length ?? 0;
        const completed = this._session.completedTasks.length;
        return {
            adventure: this._adventure,
            session: { ...this._session },
            progress: {
                completed,
                total,
                percent: total > 0 ? Math.round((completed / total) * 100) : 0,
            },
            tasksInRange: [...this._tasksInRange],
            nextTask: this._nextTask,
            nextTaskDistance: this._nextTaskDistance,
            isActive: this._adventure !== null,
            isSpeaking: this._isSpeaking,
            lastCompletedTask: this._lastCompletedTask,
        };
    }
    // ──────────────────────────────────────────────────────────────
    // Private helpers
    // ──────────────────────────────────────────────────────────────
    _onLocationUpdate(lat, lon) {
        if (!this._adventure)
            return;
        const openTasks = this._adventure.tasks.filter((t) => !this._session.completedTasks.includes(t.id));
        const withDistance = openTasks.map((task) => ({
            task,
            distance: AdventureManager._haversineMeters(lat, lon, task.lat, task.lng),
        }));
        withDistance.sort((a, b) => a.distance - b.distance);
        this._tasksInRange = withDistance
            .filter((entry) => entry.distance <= entry.task.triggerRadius)
            .map((entry) => entry.task);
        if (withDistance.length > 0) {
            this._nextTask = withDistance[0].task;
            this._nextTaskDistance = Math.round(withDistance[0].distance);
        }
        else {
            this._nextTask = null;
            this._nextTaskDistance = null;
        }
        this._notify();
    }
    async _speak(text) {
        if (typeof window === 'undefined' || !window.speechSynthesis)
            return;
        if (!this._audioBus.request(AdventureManager.SOURCE_ID)) {
            return;
        }
        try {
            window.speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = this._config?.ttsLang ?? DEFAULT_TTS_LANG;
            utterance.rate = this._config?.ttsRate ?? DEFAULT_TTS_RATE;
            this._currentUtterance = utterance;
            this._isSpeaking = true;
            this._notify();
            utterance.onend = () => {
                this._isSpeaking = false;
                this._currentUtterance = null;
                this._audioBus.release(AdventureManager.SOURCE_ID);
                this._notify();
            };
            utterance.onerror = () => {
                this._isSpeaking = false;
                this._currentUtterance = null;
                this._audioBus.release(AdventureManager.SOURCE_ID);
                this._notify();
            };
            window.speechSynthesis.speak(utterance);
        }
        catch {
            this._isSpeaking = false;
            this._audioBus.release(AdventureManager.SOURCE_ID);
        }
    }
    /** Called by AudioBusManager when a higher-priority source preempts us. */
    _pauseSpeechExternally() {
        if (typeof window !== 'undefined' && window.speechSynthesis) {
            window.speechSynthesis.cancel();
        }
        this._isSpeaking = false;
        this._currentUtterance = null;
        this._notify();
    }
    _persist() {
        if (!this._adventure)
            return;
        const state = {
            schema_version: ADVENTURE_STATE_SCHEMA_VERSION,
            completedTasks: this._session.completedTasks,
            startedAt: this._session.startedAt,
            lastUpdatedAt: Date.now(),
            isComplete: this._session.isComplete,
        };
        this._localState.save('adventure', this._adventure.id, state);
    }
    _notify() {
        const state = this.getState();
        for (const listener of this._listeners)
            listener(state);
    }
    static _haversineMeters(lat1, lon1, lat2, lon2) {
        const R = 6371000;
        const toRad = (d) => (d * Math.PI) / 180;
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }
}
AdventureManager.SOURCE_ID = 'audio-guide:adventure';
//# sourceMappingURL=AdventureManager.js.map